import React, { useState, useEffect, useCallback, useRef ,useMemo } from "react";

import { ProgressBar } from "react-bootstrap";
import Sound from "react-sound";
import { base, s3base } from "api.js";
import NoMicrophone from "views/NoMicrophone.jsx";
import PlaybackSpeedBtns from "components/PracticeMaterial/PlaybackSpeedBtns.jsx";
import ReusableRecorder from "components/Recorder/ReusableRecorder";
import Beep from "Beep.js";

import "components/custom.css";
import MicWaveform from "../MicWaveform";
import RecordedWaveform from "components/Question/Speaking/RecordedWaveform";
import IOSAudioPlayer from "./IOSAudioPlayer.jsx"; // reuse the same component
import { isIOSDevice } from 'util/deviceDetection';
import useAudioDuration from "hooks/useAudioDuration";

const SummarizeGroupDiscussion = (props) => {
  const [isRecording, setIsRecording] = useState(false);
  const [blobURL, setBlobURL] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);
  const [question, setQuestion] = useState("");
  const [secondCount, setSecondCount] = useState(8);
  const [duration, setDuration] = useState(0);
  const [secondCount2, setSecondCount2] = useState(0);
  const [recordCount, setRecordCount] = useState(0);
  const [recordId, setRecordId] = useState(1);
  const [intervalId, setIntervalId] = useState(0);
  const [intervalId2, setIntervalId2] = useState(0);
  const [questionId, setQuestionId] = useState("");
  const [reset, setReset] = useState(false);
  const [soundStatus, setSoundStatus] = useState(true);
  const [soundPer, setSoundPer] = useState(0);
  const [trans, setTrans] = useState("");
  const [volume, setVolume] = useState(90);
  const [correctFlag, setCorrectFlag] = useState(true);
  const [beepStatus, setBeepStatus] = useState(false);
  const [position, setPosition] = useState(0);
  const [file, setFile] = useState("");
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [soundKey, setSoundKey] = useState(0);
  const [secondCount2Bckup, setSecondCount2Bckup] = useState(0);
  const [saveTranscript, setSaveTranscriptState] = useState(false);
  const [micPermitted, setMicPermitted] = useState(null);
  const [browserAllowed, setBrowserAllowed] = useState(true);
  const [audioFinished, setAudioFinished] = useState(false);
  const processStartedRef = useRef(false);
  const { audioDuration, audioSecondsLeft, handleAudioPlaying, resetAudioDuration } = useAudioDuration();

    // Mobile‑specific states
  const [iosStarted, setIosStarted] = useState(false);
  const [showIosStartButton, setShowIosStartButton] = useState(false);
  const audioContextRef = useRef(null);
  const iosAudioElementRef = useRef(null);
  const unlockedAudioRef = useRef(null); // pre-unlocked Audio element for iOS autoplay
  const handleRecordingCompleteRef = useRef(null);

  const { currentQuestion, total_questions } = props;
  const LIMIT = 120;

  // Refs to store mutable values and prevent dependency issues
  const recorderRef = useRef(null);
  const intervalsRef = useRef({
  intervalId: 0,
  intervalId2: 0,
  recordId: 0
  });

  // Store latest values in refs to avoid stale closures
  const latestValues = useRef({
  isBlocked,
  isRecording,
  secondCount,
  secondCount2,
  secondCount2Bckup,
  recordCount,
  soundStatus,
  trans,
  file,
  duration,
  blobURL
  });

  // Update refs when values change
  useEffect(() => {
  latestValues.current = {
    isBlocked,
    isRecording,
    secondCount,
    secondCount2,
    secondCount2Bckup,
    recordCount,
    soundStatus,
    trans,
    file,
    duration,
    blobURL
  };
  });

  // Timer functions that don't depend on state
  const timer = useCallback(() => {
  setSecondCount(prevSecondCount => {
    if (prevSecondCount === 0) {
    clearInterval(intervalsRef.current.intervalId);
    intervalsRef.current.intervalId = 0;
    return prevSecondCount;
    } else {
    return prevSecondCount - 1;
    }
  });
  }, []);

  const startRecordingNow = useCallback(() => {
    if (latestValues.current.isBlocked) return;

    if (recorderRef.current) {
      if (isIOSDevice() && audioContextRef.current) {
        audioContextRef.current.resume().then(() => {
          recorderRef.current.startRecording();
        }).catch(() => {
          recorderRef.current.startRecording();
        });
      } else {
        recorderRef.current.startRecording();
      }
    }

    setIsRecording(true);
    setBlobURL("");
    setRecordCount(0);

  // Clear any existing recording interval
  if (intervalsRef.current.recordId) {
    clearInterval(intervalsRef.current.recordId);
  }

  // Start recording timer
  const newRecordId = setInterval(() => {
    setRecordCount(prevRecordCount => {
      if (prevRecordCount === LIMIT) {
        clearInterval(intervalsRef.current.recordId);
        intervalsRef.current.recordId = 0;
        if (recorderRef.current) {
          recorderRef.current.stopRecording();
        }
        return prevRecordCount;
      }
      return prevRecordCount + 1;
    });
  }, 1000);

  intervalsRef.current.recordId = newRecordId;
  setRecordId(newRecordId);
}, [isIOSDevice]);

  const handleBeepFinishedPlaying = useCallback(() => {
    // Recording already started when beep began — just clear beep status
    setBeepStatus(false);
  }, []);

    const createAndPlayBeep = useCallback((audioContext) => {
    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = "sine";
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      setTimeout(() => {
        setBeepStatus(false);
      }, 300);
    } catch (error) {
      console.error('Error playing beep sound:', error);
      setBeepStatus(false);
    }
  }, [handleBeepFinishedPlaying]);

    // Mobile beep using Web Audio API
  const playBeepSound = useCallback(() => {
    if (isIOSDevice()) {
      // iOS needs explicit AudioContext resume
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        let audioContext = audioContextRef.current;
        if (!audioContext) {
          audioContext = new AudioContext();
          audioContextRef.current = audioContext;
        }
        if (audioContext.state === "suspended") {
          audioContext.resume().then(() => {
            createAndPlayBeep(audioContext);
          }).catch(err => {
            console.error('Failed to resume AudioContext:', err);
            setBeepStatus(false);
          });
        } else {
          createAndPlayBeep(audioContext);
        }
      } catch (error) {
        console.error('Mobile beep error:', error);
        setTimeout(() => {
          setBeepStatus(false);
        }, 300);
      }
    } else {
      // Android – try AudioContext, fallback to Sound if needed
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        let audioContext = audioContextRef.current;
        if (!audioContext) {
          audioContext = new AudioContext();
          audioContextRef.current = audioContext;
        }
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            createAndPlayBeep(audioContext);
          }).catch(() => {
            createAndPlayBeep(audioContext); // try anyway
          });
        } else {
          createAndPlayBeep(audioContext);
        }
      } catch (error) {
        console.error('Android beep error, falling back to Sound', error);
        setBeepStatus(true); // fallback to Sound component
      }
    }
  }, [createAndPlayBeep, handleBeepFinishedPlaying, isIOSDevice]);

  const timer1 = useCallback(() => {
    setSecondCount2(prevSecondCount2 => {
      if (prevSecondCount2 === 0) {
        clearInterval(intervalsRef.current.intervalId2);
        intervalsRef.current.intervalId2 = 0;
        // Start recording immediately when beep plays so no words are missed
        startRecordingNow();
        if (isIOSDevice()) {
          playBeepSound();
        } else {
          setBeepStatus(true);
        }
        setSoundStatus(false);

        return prevSecondCount2;
      } else {
        if (latestValues.current.secondCount2Bckup === 0) {
          setSecondCount2Bckup(prevSecondCount2);
        }
        return prevSecondCount2 - 1;
      }
    });
  }, []);

  const startProcess = useCallback(() => {
  if (!props.answer && props.data?.media_link) {
    const newIntervalId = setInterval(timer, 1000);
    intervalsRef.current.intervalId = newIntervalId;
    setIntervalId(newIntervalId);

    const audio_url = s3base + props.data.media_link;
    props.updateQuestionAudioUrlInPractice?.(audio_url);
  }
  }, [props.answer, props.data?.media_link, props.updateQuestionAudioUrlInPractice, timer]);

  // Initial mount effect
  // useEffect(() => {
  // // check browser. Only Chrome only.

  // // mic access.
  // navigator.mediaDevices
  //   .getUserMedia({ audio: true })
  //   .then(() => {
  //   // Permission Granted
  //   setIsBlocked(false);
  //   setMicPermitted(true);
  //   setBrowserAllowed(true);

  //   // if (chrome) {}
  //   })
  //   .catch(() => {
  //   // Permission Denied
  //   setIsBlocked(true);
  //   setMicPermitted(false);
  //   setBrowserAllowed(true);
  //   });

  // // Cleanup function for componentWillUnmount
  // return () => {
  //   if (intervalsRef.current.intervalId) {
  //   clearInterval(intervalsRef.current.intervalId);
  //   }
  //   if (intervalsRef.current.intervalId2) {
  //   clearInterval(intervalsRef.current.intervalId2);
  //   }
  //   if (intervalsRef.current.recordId) {
  //   clearInterval(intervalsRef.current.recordId);
  //   }
  // };
  // }, []);

  useEffect(() => {   
  const mobile = isIOSDevice();
  setShowIosStartButton(mobile);

  if (!mobile) {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(() => {
        setIsBlocked(false);
        setMicPermitted(true);
        setBrowserAllowed(true);
      })
      .catch(() => {
        setIsBlocked(true);
        setMicPermitted(false);
        setBrowserAllowed(true);
      });
  } else {
    // On mobile, we don't ask for permission yet
    setMicPermitted(null);
    setIsBlocked(false);
    setBrowserAllowed(true); // Assume browser is allowed
  }

  return () => {
    if (intervalsRef.current.intervalId) clearInterval(intervalsRef.current.intervalId);
    
    if (intervalsRef.current.intervalId2) clearInterval(intervalsRef.current.intervalId2);
    
    if (intervalsRef.current.recordId) clearInterval(intervalsRef.current.recordId);
    
    if (audioContextRef.current) audioContextRef.current.close();
  };
}, [isIOSDevice()]);

  // Start process when permissions are granted
  // useEffect(() => {
  // if (micPermitted && browserAllowed) {
  //   startProcess();
  // }
  // }, [micPermitted, browserAllowed, startProcess]);

  useEffect(() => {
  // Only start if we have an audio file and haven't started yet
  if (props.data?.media_link && !processStartedRef.current) {
    if (!isIOSDevice()) {
      // Desktop: require microphone permission
      if (micPermitted && browserAllowed) {
        processStartedRef.current = true;
        startProcess();
      }
    } else {
      // Mobile: start immediately without waiting for permission
      if (!iosStarted) {
          setShowIosStartButton(true);
      }
    }
  }
}, [isIOSDevice(), micPermitted, browserAllowed, iosStarted, startProcess]);

  // Handle props changes (getDerivedStateFromProps equivalent)
  const prevQuestionId = useRef(questionId);
  const prevRestart = useRef(props.restart);

//   useEffect(() => {
// 		console.log("props.restart===",props.restart);
//     if (props.data) {
//       const currentQuestionId = props.data.id;
//       if (
//         (questionId && questionId !== currentQuestionId) ||
//         (props.restart && props.restart !== prevRestart.current)
//       ) {
//         // Clear all intervals
//         if (intervalsRef.current.intervalId) clearInterval(intervalsRef.current.intervalId);
//         if (intervalsRef.current.intervalId2) clearInterval(intervalsRef.current.intervalId2);
//         if (intervalsRef.current.recordId) clearInterval(intervalsRef.current.recordId);

//         intervalsRef.current = { intervalId: 0, intervalId2: 0, recordId: 0 };

//         setCorrectFlag(true);
//         setRecordCount(0);
//         setSoundPer(0);
//         setIsRecording(false);
//         setSecondCount(8);
//         setSecondCount2(10);
//         setQuestionId(currentQuestionId);
//         setBlobURL("");
//         setReset(true);
//         setTrans("");
//         setSoundStatus(true);
//         setIntervalId2(0);
//         setDuration(0);
//         setIntervalId(0);
//       } else {
//         if (!questionId) {
//           setCorrectFlag(true);
//           setQuestionId(currentQuestionId);
//         }
//       }
//     }

//     prevQuestionId.current = questionId;
//     prevRestart.current = props.restart;
//   }, [props.data?.id, props.restart, questionId]);

  // // Handle reset state
  // useEffect(() => {
  //   if (reset) {
  //     const newIntervalId = setInterval(timer, 1000);
  //     intervalsRef.current.intervalId = newIntervalId;
  //     setIntervalId(newIntervalId);
  //     setReset(false);
  //   }
  // }, [reset, timer]);

useEffect(() => {
  if (props.data) {
    const currentQuestionId = props.data.id;
    if (
      (questionId && questionId !== currentQuestionId) ||
      (props.restart && props.restart !== prevRestart.current)
    ) {
      // Clear all intervals
      if (intervalsRef.current.intervalId) clearInterval(intervalsRef.current.intervalId);
      if (intervalsRef.current.intervalId2) clearInterval(intervalsRef.current.intervalId2);
      if (intervalsRef.current.recordId) clearInterval(intervalsRef.current.recordId);
      intervalsRef.current = { intervalId: 0, intervalId2: 0, recordId: 0 };

      // Reset state
      setCorrectFlag(true);
      setRecordCount(0);
      setSoundPer(0);
      setIsRecording(false);
      setSecondCount(8);
      setSecondCount2(0);
      setSecondCount2Bckup(0);
      setQuestionId(currentQuestionId);
      setBlobURL("");
      setTrans("");
      setSoundStatus(true);
      setAudioFinished(false);
      setBeepStatus(false);
      setFile("");
      setDuration(0);
      setPlaybackSpeed(1);
      setSoundKey(prev => prev + 1);

      // Allow the start effect to run again
      processStartedRef.current = false;
      setIosStarted(false); // On mobile, show start button again

    } else if (!questionId) {
      // First load: set the question id but do not reset
      setCorrectFlag(true);
      setQuestionId(currentQuestionId);
    }
  }

  prevQuestionId.current = questionId;
  prevRestart.current = props.restart;
}, [props.data, props.restart, questionId]);

  // // Handle restart and stopRecording props
  useEffect(() => {
    if (props.restart) {
      props.restartQue?.();
    }
  }, [props.restart, props.restartQue]);

  // useEffect(() => {
  //   if (props.stopRecording) {
  //     stopTest();
  //   }
  // }, [props.stopRecording]);

  // Handle correctFlag and question updates
  // useEffect(() => {
  //   if (props.data && correctFlag) {
  //     props.submit?.({ data: "", audio_text: "", duration: 0 });
  //     setCorrectFlag(false);
  //   }
  //   if (props.data) {
  //     setQuestion(props.data.question);
  //   }
  // }, [props.data?.id, correctFlag]);

//   const start = useCallback(() => {
// 	setBeepStatus(true);
//   }, []);

 // Mobile start button handler
  const handleIosStart = useCallback(async () => {
    try {
      // Prepare audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        audioContextRef.current = new AudioContext();
        if (audioContextRef.current.state === "suspended") {
          await audioContextRef.current.resume();
        }
        // Warm up audio system
        const osc = audioContextRef.current.createOscillator();
        const gain = audioContextRef.current.createGain();
        gain.gain.setValueAtTime(0.001, audioContextRef.current.currentTime);
        osc.connect(gain);
        gain.connect(audioContextRef.current.destination);
        osc.start();
        osc.stop(audioContextRef.current.currentTime + 0.1);
      }
       // Unlock a reusable Audio element for iOS autoplay.
      // Calling play() synchronously inside this user-gesture handler permanently
      // unlocks this Audio object so it can play freely later (e.g. in canplaythrough).
      if (!unlockedAudioRef.current) {
        const ua = new Audio();
        // Minimal silent WAV so iOS accepts the play() call
        ua.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
        const unlockPromise = ua.play();
        if (unlockPromise !== undefined) {
          unlockPromise
            .then(() => { ua.pause(); ua.currentTime = 0; })
            .catch(() => {}); // a rejected play() still partially unlocks on most iOS versions
        }
        unlockedAudioRef.current = ua;
      }
      // Request microphone permission
      const stream = await navigator.mediaDevices
        .getUserMedia({ audio: true })
        .catch(err => {
          setIsBlocked(true);
          setMicPermitted(false);
          return null;
        });
      
      if (!stream) return;

      if (stream) {
        setIsBlocked(false);
        setMicPermitted(true);
        stream.getTracks().forEach(track => track.stop()); // we'll re‑acquire later
      }

      setIosStarted(true);
      setSecondCount(1); // Shorter countdown for mobile
      setShowIosStartButton(false);

      // Small delay to let audio system settle
      // setTimeout(() => {
      //   if (micPermitted && browserAllowed) {
      //     startProcess();
      //   }
      // }, 100);
       processStartedRef.current = true; // Mark process as started

      // Start the initial 8‑second countdown
      startProcess();
    } catch (error) {
      console.error("iOS start error:", error);
      setIosStarted(true); // fallback – attempt anyway
      setShowIosStartButton(false);
      processStartedRef.current = true;
      startProcess(); // fallback
    }
  }, [micPermitted, browserAllowed, startProcess]);

  const stop = useCallback(async () => {
    if(!props.testMode){
      props.setIsStopped(false)
    }
    if (!latestValues.current.isBlocked && latestValues.current.isRecording && recorderRef.current) {
      recorderRef.current.stopRecording();
    }
  }, []);

//   const setTransFunction = useCallback((a) => {
// 	if (latestValues.current.trans !== a) {
// 	  setTrans(a);
// 	}
//   }, []);

//   const stopTest = useCallback(async () => {
// 	if (!latestValues.current.isBlocked && latestValues.current.isRecording && recorderRef.current) {
// 	  recorderRef.current.stopRecording();
// 	} else {
// 	  props.nextQuestion?.({
// 		data: latestValues.current.file,
// 		audio_text: latestValues.current.trans,
// 		duration: latestValues.current.duration,
// 	  });
// 	}
//   }, [props.nextQuestion]);

  const handleSongPlaying = useCallback((obj) => {
  const p = (obj.position * 100 / obj.duration);
  setSoundPer(p);
  handleAudioPlaying(obj);
  }, []);

  const handleSongFinishedPlaying = useCallback(() => {
  setSoundStatus(false);
  props.onMainAudioFinished?.();
  props.soundChecked?.();
  setAudioFinished(true);
}, [props.onMainAudioFinished, props.soundChecked]);

const stopAudio = useCallback(() => {
  if (intervalsRef.current.intervalId2) {
    clearInterval(intervalsRef.current.intervalId2);
    intervalsRef.current.intervalId2 = 0;
  }
  resetAudioDuration();
  setSoundStatus(false);
  setSecondCount2(0);
  setAudioFinished(true);

  if (isIOSDevice()) {
    setSecondCount2(10);
    const newIntervalId = setInterval(timer1, 1000);
    intervalsRef.current.intervalId2 = newIntervalId;
    setIntervalId2(newIntervalId);
  } else {
    setSecondCount2(10);
    const newIntervalId = setInterval(timer1, 1000);
    intervalsRef.current.intervalId2 = newIntervalId;
    setIntervalId2(newIntervalId);
  }
  props.onMainAudioFinished?.();
}, [timer1, props.onMainAudioFinished, isIOSDevice(), secondCount2Bckup]);

  const skip = useCallback(() => {
  if (intervalsRef.current.intervalId) {
    clearInterval(intervalsRef.current.intervalId);
    intervalsRef.current.intervalId = 0;
  }
  setSecondCount(0);
  }, []);

  const changeRange = useCallback((e) => {
  setVolume(e.target.value);
  }, []);

  const loadingSound = useCallback((obj) => {
    let d = obj.duration / 1000 + 9;
    if (obj.duration !== 0 && intervalsRef.current.intervalId2 === 0) {
      const newIntervalId = setInterval(timer1, 1000);
      intervalsRef.current.intervalId2 = newIntervalId;
      setSoundStatus(true);
      setIntervalId2(newIntervalId);
      setSecondCount2Bckup(Math.ceil(d));
      setSecondCount2(Math.ceil(d));
    }
  }, [timer1]);

  const changePlaybackSpeed = useCallback((speed) => {
  // Don't allow speed changes if audio has finished
  if (audioFinished) return;
  
  console.log(latestValues.current.secondCount2Bckup);
  if (speed !== playbackSpeed) {
    let seconds = latestValues.current.secondCount2Bckup;
    if (latestValues.current.secondCount === 0) {
      if (speed === 2 || speed === 1.5) {
        seconds = seconds / speed;
      }
    }
    setSoundKey(prevSoundKey => prevSoundKey + 1);
    setPlaybackSpeed(speed);
    setSecondCount2(Math.round(seconds));
  }
}, [playbackSpeed, audioFinished]);

  const setSaveTranscript = useCallback((status) => {
  setSaveTranscriptState(status);
  }, []);

  const handleRecordingComplete = useCallback((data) => {
      // const { question_audio_local, defaultSelectedVoice_local } = getQuestionAudio(props.data);
  setBlobURL(data.url);
  setIsRecording(false);
  setRecordCount(100);
  setFile(data.file);
  setDuration(data.duration);
  setTrans(data.transcript);
      // setQuestionAudio(question_audio_local);
      // setDefaultSelectedVoice(defaultSelectedVoice_local);
    // Update latestValues immediately
  latestValues.current.file = data.file;
  latestValues.current.duration = data.duration;
  latestValues.current.blobURL = data.url;
  latestValues.current.trans = data.transcript; 
  const data_to_send = {
    data: data.file,
    audio_text: data.transcript,
    duration: data.duration,
    blob_url: data.url,
  }; 
  if (props.testMode) {
    props.nextQuestion?.(data_to_send);
  } else {
    props.submit?.(data_to_send, true);
    props.showResult?.();
  }
  }, [props.testMode, props.nextQuestion, props.submit, props.showResult]);
  
    handleRecordingCompleteRef.current = handleRecordingComplete;
  
    const stableHandleRecordingComplete = useCallback((data) => {
      handleRecordingCompleteRef.current(data);
    }, []);

  const playSound = useCallback(() => {
  const { resultMode, data } = props;

  let sound = false;
  // Add additional check to prevent audio conflicts
  if (!resultMode && data && soundStatus && secondCount === 0 && !audioFinished) {
    sound = true;
  }

  return sound;
  }, [props.resultMode, props.data, soundStatus, secondCount, audioFinished]);

  const playingQuestionSectionLabel = useCallback(() => {
  let status = "";

  if (!isRecording) {
    if (secondCount !== 0 && soundStatus === true) {
    status = `Begining in ${secondCount} seconds`;
    } else if (secondCount === 0 && blobURL === "" && audioFinished === false) {
    status = "Playing question audio...";
    } else {
    status = "Question audio finished."; // just any case.
    }
  } else {
    status = "Question audio finished.";
  }
  return status;
  }, [isRecording, secondCount, soundStatus, blobURL]);

  const showSkipAudioWaitButton = useCallback(() => {
  let show_button = false;

  if (secondCount !== 0 && blobURL === "" && !isRecording) {
    show_button = true;
  }

  return show_button;
  }, [secondCount, blobURL, isRecording]);

  const recordingLabel = useCallback(() => {
  let label = "";
  if (secondCount === 0 && secondCount2 !== 0) {
    label = `Recording starts in ${secondCount2} seconds...`;
  } else if (blobURL === "" && isRecording) {
    label = "Recording audio...";
  } else if (blobURL !== "" && !isRecording) {
    label = "Recording Finished.";
  } else if (audioFinished && !isRecording && blobURL === "" && secondCount === 0) {
    label = "Preparing to record...";
  }
  return label;
  }, [secondCount, secondCount2, blobURL, isRecording, audioFinished]);

  const showStopRecordingButton = useCallback(() => {
  let button = false;

  const { testMode } = props;

  if (!testMode && isRecording) {
    button = true;
  }

  return button;
  }, [props.testMode, isRecording]);

  const showRecordedAudio = useCallback(() => {
  const { testMode } = props;

  let recorded_audio = false;
  if (blobURL && !testMode) {
    recorded_audio = true;
  }

  return recorded_audio;
  }, [blobURL, props.testMode]);

  const skipAudio = useCallback(() => {
  const { testMode } = props;

  let button = false;
  if (!testMode && soundStatus === true && secondCount === 0) {
    button = true;
  }

  return button;
  }, [props.testMode, soundStatus, secondCount]);

  const playBeep = useCallback(() => {
  const { resultMode } = props;

  let beep = false;
  if (!resultMode && beepStatus) {
    beep = true;
  }
  return beep;
  }, [props.resultMode, beepStatus]);

  // Mobile start button UI
  const renderIosStartButton = () => (
    <div className="ios-start-container text-center" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "200px" }}>
      <button
        className="btn btn-primary btn-lg"
        onClick={handleIosStart}
        style={{
          padding: "15px 30px",
          fontSize: "18px",
          borderRadius: "25px",
          background: "linear-gradient(135deg, rgb(171 248 97) 0%, rgb(104 162 75) 100%)",
          border: "none",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.2)",
        }}
      >
        <i className="fa fa-play-circle me-2"></i>
        Start Practice
      </button>
      <div className="ios-instructions mt-4 p-3 bg-light rounded" style={{ maxWidth: "400px" }}>
        <small className="text-muted">
          <i className="fa fa-volume-up me-1"></i> Audio will play automatically after clicking
          <br />
          <i className="fa fa-microphone me-1 mt-1"></i> Recording will start after the question audio
        </small>
      </div>
    </div>
  );

  if (isBlocked) {
  return <NoMicrophone />;
  }

    // Show mobile start button if needed
  if (showIosStartButton && !iosStarted) {
    return <div className="pmw-tabs-section d-flex align-items-center justify-content-center">{renderIosStartButton()}</div>;
  }

  return (
  <>
      <ReusableRecorder
      ref={recorderRef}
      onRecordingComplete={stableHandleRecordingComplete}
      maxDuration={120000}
      enableSpeechRecognition={true}
      fileName="retell-lecture.mp3"
      />

      {playSound() && !isIOSDevice() && (
      <Sound
        url={s3base + props.data.media_link}
        playStatus={Sound.status.PLAYING}
        onPlaying={handleSongPlaying}
        onLoading={loadingSound}
        onFinishedPlaying={handleSongFinishedPlaying}
        volume={volume}
        playbackRate={playbackSpeed}
        key={soundKey}
      />
      )}
      {playSound() && isIOSDevice() && (
        <IOSAudioPlayer
          audioUrl={s3base + props.data.media_link}
          onPlaying={handleSongPlaying}
          onLoading={loadingSound}
          onFinishedPlaying={handleSongFinishedPlaying}
          volume={volume}
          playbackRate={playbackSpeed}
          soundKey={soundKey}
          unlockedAudio={unlockedAudioRef.current}
        />
      )}

      {!isIOSDevice() && playBeep() && (
      <Sound
        url={Beep}
        playStatus={Sound.status.PLAYING}
        onFinishedPlaying={handleBeepFinishedPlaying}
        onError={handleBeepFinishedPlaying}
      />
      )}

      <div className="question_section_height pmw-tabs-section d-flex align-items-start pmw-outer-wrapper recorded-wrapper challenge-content-wrapper visibility-visible m-0" id="retell-lec"
      // style={{
      //   minHeight: "220px", height: "100%"
      // }}
      >
      <div className="recorded-section h-100" style={{ width: "50%" }}>
        <div className="recorded-content">
        <div className="row">
          <div className="col-12">
          <ul className="recorded-list">
           <li>Status: </li>
                      <li>{playingQuestionSectionLabel()}</li>
                      {/* {secondCount === 0 && !isRecording && audioDuration > 0 && audioSecondsLeft > 0 && (
    <li>
      Audio ends in: {audioSecondsLeft}s 
    </li>
  )} */}
          </ul>
          </div>
          {/* <div className="col-2"><span className="volume-label">Volume</span></div>*/}
        </div>

        <div className="recorded-audio-wrapper" style={{ marginTop: "50px" }}>
          <div className="row">
          <div className="recorded-audio-outer">
            <ProgressBar now={soundPer} striped />

            <div className="volume-control-wrapper">
            <span className="volume-icon">
              <i className="fa fa-volume-up" aria-hidden="true"></i>
            </span>
            <div className="re-volume-wrapper">
              <input
              type="range"
              placeholder="Volume"
              className="volume-control"
              min="0"
              max="100"
              step="1"
              defaultValue={volume}
              onInput={changeRange}
              style={{
                "--progress": `${(volume / 100) * 100
                }%`,
              }}
              />
            </div>
            </div>
          </div>
          </div>

          <div className="row">
          <div className="col-12">
            <div className="mt-3 clearfix">
            <div className="pull-left" id="retell-playback">
              <PlaybackSpeedBtns
              changePlaybackSpeed={changePlaybackSpeed}
              playbackSpeed={playbackSpeed}
              audioFinished={audioFinished}
              answer={props.answer}
              sound={soundStatus}
              />
            </div>
            {showSkipAudioWaitButton() && (
              <div className="btn-outer skip-btn-wrapper retell-skip pull-right m-0">
                <button
                  type="button"
                  className="outline-btn skip-btn-new text-center visibility-visible"
                  onClick={skip}
                >
                  Skip
                </button>
              </div>
            )}
            {skipAudio() && (
              <div className="btn-outer skip-btn-wrapper retell-skip pull-right m-0">
              <button
                className="outline-btn skip-btn-new text-center visibility-visible"
                onClick={stopAudio}
              >
                Skip
              </button>
              </div>
            )}
            </div>
          </div>
          </div>
        </div>
        </div>
      </div>
      <div className="recorded-section h-100" style={{ width: "50%" }}>
        <div className="row">
        <div className="w-100">
          {/* <div> */}
          <div className="recorded-content">
          <ul className="recorded-list">
             <li>Status:<span className="text-black"> {recordingLabel()}</span></li>
                      {/* <li>{recordingLabel()}</li> */}
                       {/* {isRecording && (
    <li style={{ color: '#e74c3c', fontWeight: 'bold' }}>
      Recording: {recordCount}s / {LIMIT}s remaining: {LIMIT - recordCount}s
    </li>
  )} */}
                     {isRecording && (
    <li>
      Recording: {LIMIT - recordCount}s
    </li>
  )}
          </ul>
          <div className="recorded-audio-wrapper">
            {showRecordedAudio() ? (
            <div className="mic-waveform-wrapper" style={{ marginTop: "37px" }}>
							<RecordedWaveform
							testMode={false}
							blobURL={blobURL}
							/>
						</div>
            ) : (
            <>
              <div className="mic-waveform-wrapper d-flex" id="retell-micwave">
              {recordCount === 0 ? (
                // <img
                // src="/images/waveform.png"
                // alt="Waveform"
                // />
                <div className="waveform" style={{ marginTop: "48px" }}>
      					  {Array.from({ length: 100 }).map((_, i) => (
      					    <div key={i} className="wave-bar" />
      					  ))}
      					</div>
              ) : (
                <MicWaveform
                progressValue={recordCount * 0.8334}
                />
              )}
              {showStopRecordingButton() && (
                <div className="btn-outer">
                  <button
                    type="button"
                    className="outline-btn skip-btn-new text-center mt-2 visibility-visible"
                    onClick={stop}
                  >
                    Stop
                  </button>
                </div>
              )}
              </div>
            </>
            )}
          </div>
          </div>
        </div>
        </div>
      </div>
      </div>

  
    <div className="pmw-tabs h-100">
      {/* {micPermitted === false && (
      <div className="alert alert-danger">
        No Microphone permissions granted. Please grant microphone
        permissions and reload the page.
      </div>
      )} */}

      {browserAllowed === false && (
      <div className="alert alert-danger">
        The browser you are using is not supported. Please use Google
        Chrome.
      </div>
      )}
    </div>
    
  </>
  );
};

export default SummarizeGroupDiscussion;
